import { z } from "zod";
import { BillboardId, Cents, IsoUtc, PropertyId, Uuid } from "./primitives";
import { StatsRange } from "./analytics";
import { HexColor } from "./properties";
import { StoreThemeId } from "./world";

/**
 * Advertiser operations (M5, docs/06 section 5, docs/08 section 5, ADR-0009): account-owned advertiser
 * profiles, safe image uploads (quarantine -> re-encode -> human review), immutable approved creative
 * versions, owner placement editing, billboard day rentals and private statistics.
 * Nothing user-supplied goes live before review; approval binds an immutable creative + destination version.
 */

export const AdvertiserCategory = z.enum([
  "retail",
  "food_drink",
  "health_fitness",
  "professional_services",
  "software_web",
  "technology",
  "finance",
  "travel",
  "pets",
  "arts_culture",
  "education",
  "home_services",
  "entertainment",
  "other",
  // Restricted until the founder sets the advertiser policy (ADR-0009): submissions are accepted but cannot be
  // approved unless the category is listed in ADVERTISER_ALLOWED_RESTRICTED_CATEGORIES.
  "crypto_digital_assets",
  "gambling_betting",
]);
export type AdvertiserCategory = z.infer<typeof AdvertiserCategory>;
export const RESTRICTED_ADVERTISER_CATEGORIES: readonly AdvertiserCategory[] = ["crypto_digital_assets", "gambling_betting"];

export const AdvertiserProfile = z.object({
  advertiserId: Uuid,
  businessName: z.string().min(1).max(60),
  description: z.string().max(280),
  category: AdvertiserCategory,
  status: z.enum(["active", "suspended"]),
  createdAt: IsoUtc,
});
export type AdvertiserProfile = z.infer<typeof AdvertiserProfile>;

/** POST /advertisers (create) and PATCH /advertisers/:id (owner). Plain text only. */
export const AdvertiserProfileRequest = z
  .object({ businessName: z.string().trim().min(1).max(60), description: z.string().max(280).default(""), category: AdvertiserCategory })
  .strict();
export type AdvertiserProfileRequest = z.infer<typeof AdvertiserProfileRequest>;
export const MyAdvertisersResponse = z.object({ items: z.array(AdvertiserProfile) });
export type MyAdvertisersResponse = z.infer<typeof MyAdvertisersResponse>;

// ---------------- Uploads ----------------
export const UploadPurpose = z.enum(["logo", "billboard"]);
export type UploadPurpose = z.infer<typeof UploadPurpose>;
/** Byte limits per purpose (docs/08: e.g. 2 MB / 2048x2048 for logos). */
export const UPLOAD_LIMITS = {
  logo: { maxBytes: 2_000_000, maxPixels: 2048 },
  billboard: { maxBytes: 4_000_000, maxPixels: 4096 },
} as const;

/** POST /uploads/intents — reserve a private quarantine slot; the browser then PUTs the raw bytes to `uploadUrl`. */
export const UploadIntentRequest = z
  .object({
    purpose: UploadPurpose,
    contentType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    byteSize: z.number().int().min(1).max(UPLOAD_LIMITS.billboard.maxBytes),
  })
  .strict();
export type UploadIntentRequest = z.infer<typeof UploadIntentRequest>;
export const UploadIntentResponse = z.object({
  uploadId: Uuid,
  /** Same-origin, single-use, session-bound: `PUT /api/v1/uploads/:id/content` with the raw bytes. */
  uploadUrl: z.string(),
  maxBytes: z.number().int(),
  expiresAt: IsoUtc,
});
export type UploadIntentResponse = z.infer<typeof UploadIntentResponse>;

export const UploadStatus = z.enum(["awaiting_content", "processing", "ready", "rejected", "expired"]);
/** POST /uploads/:id/finalize and GET /uploads/:id (owner only). */
export const UploadView = z.object({
  uploadId: Uuid,
  purpose: UploadPurpose,
  status: UploadStatus,
  /** Plain-language reason when rejected (e.g. "Only PNG, JPEG or WebP images", "Image is larger than 2048 px"). */
  reason: z.string().nullable(),
  /** Owner-only private preview of the safe derivative while it is unreviewed. */
  previewUrl: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
});
export type UploadView = z.infer<typeof UploadView>;

// ---------------- Creatives ----------------
export const CreativeKind = z.enum(["storefront", "billboard"]);
export type CreativeKind = z.infer<typeof CreativeKind>;
export const CreativeReviewState = z.enum(["pending", "approved", "rejected", "revoked"]);
export type CreativeReviewState = z.infer<typeof CreativeReviewState>;

/**
 * POST /creatives — submit a NEW immutable version for review. A text-only sign (no upload) is allowed: the
 * renderer draws the business name, monogram and colours. Changing anything creates a new version.
 */
export const CreativeSubmitRequest = z
  .object({
    advertiserId: Uuid,
    kind: CreativeKind,
    title: z.string().trim().min(1).max(60),
    description: z.string().max(280).default(""),
    monogram: z.string().trim().min(1).max(3),
    brandColor: HexColor,
    accentColor: HexColor,
    /** A finalized upload of the matching purpose (logo for storefront, billboard for billboard), or null. */
    uploadId: Uuid.nullable(),
    /** HTTPS only, validated server side (docs/08 section 5); null = no website link. */
    destinationUrl: z.string().max(2048).nullable(),
  })
  .strict();
export type CreativeSubmitRequest = z.infer<typeof CreativeSubmitRequest>;

export const CreativeVersionView = z.object({
  creativeId: Uuid,
  advertiserId: Uuid,
  version: z.number().int().min(1),
  kind: CreativeKind,
  reviewState: CreativeReviewState,
  /** Reviewer's note to the owner (plain text) when rejected/revoked. */
  reviewNote: z.string().nullable(),
  restrictedCategory: z.boolean(),
  title: z.string(),
  description: z.string(),
  monogram: z.string(),
  brandColor: HexColor,
  accentColor: HexColor,
  /** Approved public derivative, or the owner's private preview while pending; null for a text-only sign. */
  imageUrl: z.string().nullable(),
  destinationHost: z.string().nullable(),
  /** Full URL is visible to the owner and reviewers only. */
  destinationUrl: z.string().nullable(),
  submittedAt: IsoUtc,
  reviewedAt: IsoUtc.nullable(),
});
export type CreativeVersionView = z.infer<typeof CreativeVersionView>;
export const MyCreativesResponse = z.object({ items: z.array(CreativeVersionView) });
export type MyCreativesResponse = z.infer<typeof MyCreativesResponse>;

/** PUT /properties/:id/placement — the CURRENT owner shows an APPROVED storefront creative on their store. */
export const PlacementUpdateRequest = z.object({ creativeId: Uuid, expectedPropertyVersion: z.number().int().min(0) }).strict();
export type PlacementUpdateRequest = z.infer<typeof PlacementUpdateRequest>;
export const PlacementUpdateResponse = z.object({ propertyId: PropertyId, creativeId: Uuid, presentationVersion: z.number().int().min(1) });
export type PlacementUpdateResponse = z.infer<typeof PlacementUpdateResponse>;

// ---------------- Review (staff with the moderator/admin role) ----------------
/**
 * The reviewer's relation to a submission (ADR-0012): someone else's; their own and refused (the default: another
 * staff account must review it); or their own and allowed (an admin while ADMIN_SELF_REVIEW_ALLOWED is on; the
 * decision is marked as a self-review in the audit log).
 */
export const SelfReviewState = z.enum(["not_own", "own_blocked", "own_allowed"]);
export type SelfReviewState = z.infer<typeof SelfReviewState>;

/**
 * Automated design review (ADR-0017): a model checks each pending design once. "suggest" only stores its verdict for
 * the human reviewer; "auto" also approves clear passes and rejects clear violations (the same audited path as a
 * person, recorded as "Townsy automated review (<model>)"). Anything unclear is held for a person.
 */
export const AiReviewMode = z.enum(["suggest", "auto"]);
export type AiReviewMode = z.infer<typeof AiReviewMode>;
export const AiReviewVerdict = z.enum(["approve", "reject", "escalate"]);
export type AiReviewVerdict = z.infer<typeof AiReviewVerdict>;
/** What the review did: running; stored for a person (suggest); approved / rejected automatically; held for a person. */
export const AiReviewOutcome = z.enum(["running", "suggested", "auto_approved", "auto_rejected", "held_for_human"]);
export type AiReviewOutcome = z.infer<typeof AiReviewOutcome>;
/** Why the model gave no usable verdict. */
export const AiReviewFailure = z.enum(["timeout", "api_error", "refusal", "unparsable", "image_unavailable", "abandoned"]);
export type AiReviewFailure = z.infer<typeof AiReviewFailure>;
/** Why an "auto" review left the decision to a person. */
export const AiReviewHoldReason = z.enum([
  "no_verdict",
  "escalated",
  "low_confidence",
  "flags_disagree",
  "changed",
  "category_policy",
  "resubmitted",
  "image_not_published",
  "decision_failed",
  /** ADR-0028 (offers only): a partner-confirmed offer rewards a step on the sponsor's site Townsy cannot see: a person decides. */
  "partner_offer",
]);
export type AiReviewHoldReason = z.infer<typeof AiReviewHoldReason>;
/** A reviewer's resolution of an urgent flag (possible sexual content involving minors). */
export const AiFlagResolution = z.enum(["confirmed", "false_positive"]);
export type AiFlagResolution = z.infer<typeof AiFlagResolution>;
export const AiReviewFlags = z.object({ adult: z.boolean(), minors: z.boolean(), illegal: z.boolean(), scam: z.boolean(), hate: z.boolean() });
export type AiReviewFlags = z.infer<typeof AiReviewFlags>;
export const AiReviewView = z.object({
  model: z.string(),
  mode: AiReviewMode,
  outcome: AiReviewOutcome,
  /** Null while running or when the model gave no usable answer (see `failure`). */
  verdict: AiReviewVerdict.nullable(),
  flags: AiReviewFlags.nullable(),
  /** 0..1 as reported by the model (stored in thousandths). */
  confidence: z.number().min(0).max(1).nullable(),
  /** The model's short reasons (plain text, staff only). */
  reasons: z.array(z.string()),
  failure: AiReviewFailure.nullable(),
  holdReason: AiReviewHoldReason.nullable(),
  /** Possible sexual content involving minors: shown first, image hidden until asked, never published automatically. */
  urgent: z.boolean(),
  urgentResolution: AiFlagResolution.nullable(),
  reviewedAt: IsoUtc.nullable(),
});
export type AiReviewView = z.infer<typeof AiReviewView>;
/** POST /review/creatives/:id/ai-flag (staff): close an urgent flag with a required note. Audited. */
export const AiFlagResolveRequest = z.object({ resolution: AiFlagResolution, note: z.string().trim().min(1).max(500) }).strict();
export type AiFlagResolveRequest = z.infer<typeof AiFlagResolveRequest>;

/**
 * ADR-0020: what became of a design submitted from an owner's store screen: pending (applied automatically on approval),
 * applied, rejected, superseded (a newer save, or a design the owner put on the store from the dashboard, replaced it) or
 * canceled (the store changed hands before it was approved).
 */
export const StoreSetupRequestState = z.enum(["pending", "applied", "rejected", "superseded", "canceled"]);
export type StoreSetupRequestState = z.infer<typeof StoreSetupRequestState>;

/** The review queue's note on a store-screen design (ADR-0020). */
export const ReviewStoreSetupNote = z.object({ propertyId: PropertyId, address: z.string(), state: StoreSetupRequestState });
export type ReviewStoreSetupNote = z.infer<typeof ReviewStoreSetupNote>;

export const ReviewQueueItem = z.object({
  creative: CreativeVersionView,
  advertiser: AdvertiserProfile,
  submittedByLabel: z.string(),
  /** Staff-only private preview URL (never public before approval). */
  imagePreviewUrl: z.string().nullable(),
  /** True when the category is restricted and not yet allowed by policy: approve is refused. */
  approvalBlockedByPolicy: z.boolean(),
  /** Whether the reviewer reading the queue submitted this design, and if so whether they may decide it. */
  selfReview: SelfReviewState,
  /** The automated review of this version (ADR-0017), or null when none ran. */
  aiReview: AiReviewView.nullable(),
  /** Who made the latest decision: a staff account or the automated review (null while pending). */
  decidedBy: z.enum(["staff", "automated"]).nullable(),
  /**
   * ADR-0020: a design submitted from an owner's "Set up your store" screen. While `state` is pending, approving it puts it on
   * that store automatically (if the submitter still owns it); otherwise it is an ordinary design. Absent for other designs.
   */
  storeSetup: ReviewStoreSetupNote.nullish(),
});
export type ReviewQueueItem = z.infer<typeof ReviewQueueItem>;
export const ReviewQueueResponse = z.object({ items: z.array(ReviewQueueItem), nextCursor: z.string().nullable() });
export type ReviewQueueResponse = z.infer<typeof ReviewQueueResponse>;
/** POST /review/creatives/:id/decision (staff). Audited. A note is required to reject or revoke. */
export const ReviewDecisionRequest = z
  .object({ decision: z.enum(["approve", "reject", "revoke"]), note: z.string().max(500).default("") })
  .strict();
export type ReviewDecisionRequest = z.infer<typeof ReviewDecisionRequest>;

// ---------------- Billboards ----------------
export const BillboardView = z.object({
  billboardId: BillboardId,
  label: z.string(),
  dayRateCents: Cents,
  allowedTermDays: z.array(z.number().int()),
  /** What shows today (approved billboard creative of the active rental), or null = "Advertise here". */
  current: z
    .object({ creativeId: Uuid, title: z.string(), imageUrl: z.string().nullable(), brandColor: HexColor, accentColor: HexColor, monogram: z.string(), until: z.string() })
    .nullable(),
});
export type BillboardView = z.infer<typeof BillboardView>;
export const BillboardListResponse = z.object({ items: z.array(BillboardView), today: z.string() });
export type BillboardListResponse = z.infer<typeof BillboardListResponse>;

export const UtcDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const BillboardAvailabilityResponse = z.object({
  billboardId: BillboardId,
  from: UtcDate,
  to: UtcDate,
  days: z.array(z.object({ date: UtcDate, state: z.enum(["available", "held", "booked"]) })),
});
export type BillboardAvailabilityResponse = z.infer<typeof BillboardAvailabilityResponse>;

// ---------------- Statistics ----------------
/** GET /me/advertiser-stats — owner-only, current and own past intervals; never another owner's interval. */
export const AdvertiserStatsResponse = z.object({
  definitionsVersion: z.number().int(),
  timezone: z.literal("UTC"),
  generatedAt: IsoUtc,
  lastAggregatedAt: IsoUtc.nullable(),
  items: z.array(
    z.object({
      propertyId: PropertyId.nullable(),
      billboardId: BillboardId.nullable(),
      label: z.string(),
      /** The account's own ownership/rental period this row covers. */
      periodStart: IsoUtc,
      periodEnd: IsoUtc.nullable(),
      current: z.boolean(),
      ranges: z.record(
        StatsRange,
        z.object({
          signImpressions: z.number().int().min(0).nullable(),
          panelViews: z.number().int().min(0).nullable(),
          roomEntries: z.number().int().min(0).nullable(),
          outboundClicksOrdinary: z.number().int().min(0).nullable(),
          outboundClicksRewarded: z.number().int().min(0).nullable(),
        }),
      ),
    }),
  ),
  notes: z.array(z.string()),
});
export type AdvertiserStatsResponse = z.infer<typeof AdvertiserStatsResponse>;

// ---------------- "Your store": one screen (ADR-0020, founder feedback 2026-10-14) ----------------

/**
 * The single plain status the owner sees: what the town shows and what happens next.
 *  - not_set_up: nothing saved yet (visitors see the owner's name and "NEW");
 *  - live: the town shows the owner's sign;
 *  - checking: the automated check is running (usually under a minute);
 *  - waiting: a Townsy reviewer decides (automated check off, in suggest mode, unsure, or the category needs a person);
 *  - needs_change: the latest change was not accepted (or was taken down); `reason` says what to fix.
 */
export const StoreSetupState = z.enum(["not_set_up", "live", "checking", "waiting", "needs_change"]);
export type StoreSetupState = z.infer<typeof StoreSetupState>;
export const StoreSetupStatus = z.object({
  state: StoreSetupState,
  /** needs_change: the reviewer's or the automated check's note (plain text). */
  reason: z.string().nullable(),
  /** waiting: the category cannot go live until Townsy's advertiser policy allows it. */
  categoryPolicy: z.boolean(),
  /** The town still shows an earlier sign of this owner while a change waits or needs fixing. */
  previousSignShowing: z.boolean(),
  /** When the change the status is about was saved (null for not_set_up). */
  since: IsoUtc.nullable(),
});
export type StoreSetupStatus = z.infer<typeof StoreSetupStatus>;

export const STORE_SETUP_LIMITS = { businessName: 60, description: 280, website: 2048, signTitle: 60, signMessage: 280 } as const;

/**
 * ADR-0031 (founder 2026-10-20: "Yes, reuse the store design too"): the owner's OWN design from an earlier store (a store
 * they lost, or another store they own) to put on this one. Only ever the requesting account's own approved, live design
 * (never another account's). Saved unchanged it goes on this store at once: it was already checked, exactly like "Show
 * on my store" (ADR-0009 #8); changed, it is a new design that is checked first (ADR-0020).
 */
export const StoreSetupReuse = z.object({
  fromPropertyId: PropertyId,
  fromAddress: z.string(),
  /** The approved design to put on this store (the save's `reuseCreativeId`). */
  creativeId: Uuid,
  businessName: z.string().max(60),
  description: z.string().max(280),
  website: z.string().nullable(),
  /** Null for an older design made before categories (the owner then picks one, and it is checked first). */
  category: AdvertiserCategory.nullable(),
  brandColor: HexColor,
  accentColor: HexColor,
  logo: z.object({ uploadId: Uuid, previewUrl: z.string().nullable() }).nullable(),
  /** The sign inside and the interior style of that store (plain text; they change at once, as always). */
  sign: z.object({ title: z.string().max(60), message: z.string().max(280) }),
  interiorTheme: StoreThemeId,
  /** ADR-0030: the owner also has earlier offers to re-use here once the store is live (their number). */
  offers: z.number().int().min(0),
});
export type StoreSetupReuse = z.infer<typeof StoreSetupReuse>;

/** GET /api/v1/stores/:propertyId/setup — the current owner's one-screen form (prefilled) and status. */
export const StoreSetupView = z.object({
  propertyId: PropertyId,
  address: z.string(),
  businessName: z.string().max(60),
  description: z.string().max(280),
  /** The website the owner entered (full https URL; private to the owner), or null. */
  website: z.string().nullable(),
  /** Null until the owner has chosen one (a new store). */
  category: AdvertiserCategory.nullable(),
  brandColor: HexColor,
  accentColor: HexColor,
  /** The logo of the current form (owner-only private preview), or null for a name-only sign. */
  logo: z.object({ uploadId: Uuid, previewUrl: z.string().nullable() }).nullable(),
  /** The in-store sign (the TV inside); version 0 = none yet. Plain text. */
  sign: z.object({ title: z.string().max(60), message: z.string().max(280), version: z.number().int().min(0) }),
  interiorTheme: StoreThemeId,
  status: StoreSetupStatus,
  /** What the town shows on the building right now (public presentation). */
  live: z.object({
    businessName: z.string(),
    logoUrl: z.string().nullable(),
    websiteHost: z.string().nullable(),
    description: z.string(),
    monogram: z.string(),
    brandColor: HexColor,
    accentColor: HexColor,
    placeholder: z.boolean(),
  }),
  /** Whether the automated check is on in auto mode on this server (the screen says "usually under a minute"). */
  quickCheck: z.boolean(),
  /** ADR-0031: the owner's own design from an earlier store, offered while this store shows no design of theirs yet. */
  reuse: StoreSetupReuse.nullable(),
});
export type StoreSetupView = z.infer<typeof StoreSetupView>;

/**
 * PUT /api/v1/stores/:propertyId/setup (current owner only; Idempotency-Key required). One Save: the name, logo, website,
 * description, category and colour become a new design that goes live automatically once it passes the check; the
 * in-store sign and the interior change at once (as before).
 */
export const StoreSetupRequest = z
  .object({
    businessName: z.string().trim().min(1).max(60),
    description: z.string().max(280).default(""),
    website: z.string().max(2048).nullable(),
    category: AdvertiserCategory,
    /** A finalized logo upload of this account ("ready"), or null for a name-only sign. */
    logoUploadId: Uuid.nullable(),
    brandColor: HexColor,
    sign: z.object({ title: z.string().max(60), message: z.string().max(280) }).strict(),
    /** The in-store sign version the form started from (a concurrent edit is reported, not overwritten). */
    expectedSignVersion: z.number().int().min(0),
    interiorTheme: StoreThemeId.optional(),
    /**
     * ADR-0031 "Re-use your store design": the form was filled from this approved design of the SAME account. Unchanged,
     * it goes on the store at once (already checked); changed, a new design is checked first. Audited; the server
     * refuses a design that is not the account's own approved, live one.
     */
    reuseCreativeId: Uuid.nullable().optional(),
  })
  .strict();
export type StoreSetupRequest = z.infer<typeof StoreSetupRequest>;

export const StoreSetupResponse = z.object({
  setup: StoreSetupView,
  saved: z.object({
    /**
     * submitted: a new design is being checked; unchanged: the name/logo/website/description were already as sent;
     * applied (ADR-0031): the owner's own already-checked design went on the store at once.
     */
    design: z.enum(["submitted", "unchanged", "applied"]),
    sign: z.boolean(),
    interior: z.boolean(),
  }),
});
export type StoreSetupResponse = z.infer<typeof StoreSetupResponse>;
